/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LoginIcon from '@mui/icons-material/Login';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { STATUS_HEX, TEAL_BORDER } from '../constants';

const STEPS = [
  {
    status: 'SCHEDULED',
    icon: EventAvailableIcon,
    title: 'Scheduled',
    description: 'Patient is booked against a doctor for a date & time. An appointment number is generated.',
  },
  {
    status: 'CHECKED_IN',
    icon: LoginIcon,
    title: 'Checked-In',
    description: 'Reception confirms the patient has arrived. A clinic Visit is tagged at this point (or was already tagged at booking, for same-day appointments).',
  },
  {
    status: 'ATTENDED',
    icon: MedicalServicesIcon,
    title: 'Attended',
    description: 'The doctor has started seeing the patient. A prescription can now be created from this appointment.',
  },
  {
    status: 'COMPLETED',
    icon: CheckCircleIcon,
    title: 'Completed',
    description: 'Consultation finished — checkout done. This is a terminal state; nothing can change after this.',
  },
];

export default function LifecycleInfoDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Appointment Lifecycle
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pl: 1 }}>
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const color = STATUS_HEX[step.status];
            return (
              <Box key={step.status} sx={{ display: 'flex', gap: 2, position: 'relative', pb: index < STEPS.length - 1 ? 3.5 : 0 }}>
                {index < STEPS.length - 1 && (
                  <Box sx={{ position: 'absolute', left: 19, top: 40, bottom: 0, width: 2, bgcolor: TEAL_BORDER }} />
                )}
                <Box
                  sx={{
                    width: 40, height: 40, minWidth: 40, borderRadius: '50%',
                    bgcolor: `${color}18`, border: `2px solid ${color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                  }}
                >
                  <Icon sx={{ color, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color }}>{step.title}</Typography>
                  <Typography variant="body2" color="textSecondary">{step.description}</Typography>
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', gap: 2, mt: 3, pt: 3, borderTop: `1px dashed ${TEAL_BORDER}` }}>
            <Box
              sx={{
                width: 40, height: 40, minWidth: 40, borderRadius: '50%',
                bgcolor: `${STATUS_HEX.CANCELLED}18`, border: `2px solid ${STATUS_HEX.CANCELLED}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CancelIcon sx={{ color: STATUS_HEX.CANCELLED, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: STATUS_HEX.CANCELLED }}>
                Cancelled — reachable from any non-terminal stage
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Scheduled, Checked-In or Attended appointments can be cancelled with a reason at any time.
                Anything still open at midnight is automatically cancelled by the system.
                Once Completed, an appointment can never be cancelled.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
